import Button from "components/form/Button";
import CustomModal from "components/ui-component/CustomModal";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";

const SIGN_UP_SESSION_TIME_KEY = "dcdial-sign-up-session-time";
const SIGN_UP_SESSION_EXPIRED_KEY = "dcdial-sign-up-session-expired";
const SESSION_WARNING_TIMEOUT = 29 * 60 * 1000;
const SESSION_EXPIRY_TIMEOUT = SESSION_WARNING_TIMEOUT + 60 * 1000;
const ACTIVITY_THROTTLE_TIMEOUT = 1000;

type SessionState = "active" | "warning" | "expired";

const getStoredSessionTime = () => {
  const storedTime = localStorage.getItem(SIGN_UP_SESSION_TIME_KEY);
  const parsedTime = storedTime ? Number.parseInt(storedTime, 10) : NaN;

  return Number.isFinite(parsedTime) ? parsedTime : null;
};

const SignUpSessionRoute = ({ children }: { children: ReactNode }) => {
  const { clusterId } = useParams();
  const navigate = useNavigate();
  const [sessionState, setSessionState] = useState<SessionState>(() =>
    localStorage.getItem(SIGN_UP_SESSION_EXPIRED_KEY) ? "expired" : "active"
  );
  const [lastActivity, setLastActivity] = useState(() => {
    const storedTime = getStoredSessionTime();

    if (storedTime) {
      return storedTime;
    }

    const now = Date.now();
    localStorage.setItem(SIGN_UP_SESSION_TIME_KEY, `${now}`);
    return now;
  });
  const lastActivityWriteRef = useRef(0);

  const expireSession = useCallback(() => {
    localStorage.removeItem(SIGN_UP_SESSION_TIME_KEY);
    localStorage.setItem(SIGN_UP_SESSION_EXPIRED_KEY, "true");
    setSessionState("expired");

    if (clusterId) {
      navigate(`/${clusterId}/sign-up`, { replace: true });
    }
  }, [clusterId, navigate]);

  const restartSession = useCallback(() => {
    const now = Date.now();
    localStorage.removeItem(SIGN_UP_SESSION_EXPIRED_KEY);
    localStorage.setItem(SIGN_UP_SESSION_TIME_KEY, `${now}`);
    setLastActivity(now);
    setSessionState("active");

    if (clusterId) {
      navigate(`/${clusterId}/sign-up`, { replace: true });
    }
  }, [clusterId, navigate]);

  useEffect(() => {
    if (sessionState === "expired") {
      return;
    }

    const elapsed = Date.now() - lastActivity;

    if (sessionState === "warning") {
      const expiryDelay = SESSION_EXPIRY_TIMEOUT - elapsed;

      if (expiryDelay <= 0) {
        expireSession();
        return;
      }

      const expiryTimeout = window.setTimeout(expireSession, expiryDelay);
      return () => window.clearTimeout(expiryTimeout);
    }

    const warningDelay = SESSION_WARNING_TIMEOUT - elapsed;

    if (warningDelay <= 0) {
      setSessionState("warning");
      return;
    }

    const activityEvents = [
      "click",
      "keydown",
      "mousemove",
      "scroll",
      "touchstart",
    ];
    const handleActivity = () => {
      const now = Date.now();

      if (now - lastActivityWriteRef.current < ACTIVITY_THROTTLE_TIMEOUT) {
        return;
      }

      lastActivityWriteRef.current = now;
      localStorage.setItem(SIGN_UP_SESSION_TIME_KEY, `${now}`);
      setLastActivity(now);
    };
    const warningTimeout = window.setTimeout(
      () => setSessionState("warning"),
      warningDelay
    );

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, true);
    });

    return () => {
      window.clearTimeout(warningTimeout);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity, true);
      });
    };
  }, [expireSession, lastActivity, sessionState]);

  return (
    <>
      {children}
      <CustomModal
        signModal={true}
        isSessionModal={true}
        isOpen={sessionState !== "active"}
        title="Session Timeout"
        className="max-w-[880px] w-full max-h-[800px] h-fit rounded-[10px]"
        onCloseClick={() => {}}
      >
        <div className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]">
          {sessionState === "warning"
            ? "Your sign-up session is about to expire due to inactivity."
            : "Your sign-up session has expired due to inactivity. Please start again to keep your account secure."}
        </div>
        <div className="w-full flex justify-end items-center lg:pt-[30px] pt-5">
          <Button
            text={
              sessionState === "warning"
                ? "Continue sign up"
                : "Start sign up again"
            }
            onClick={restartSession}
            className="lg:w-fit w-full"
          />
        </div>
      </CustomModal>
    </>
  );
};

export default SignUpSessionRoute;
